import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../contexts/I18nContext";
import {
  Scale, Shield, Building2, Layers, FileCheck2, Gavel, Users,
  Home, Heart, Briefcase, ArrowRight, Check, Sparkles,
  ClipboardList, BookOpen, FileSearch, Microscope, AlertTriangle,
  ListChecks, Quote, Info,
} from "lucide-react";

const REPORT_SECTIONS_EL = [
  "Εκτελεστική Περίληψη", "Γεγονότα", "Νομικά Ζητήματα",
  "Εφαρμοστέο Κυπριακό Δίκαιο", "Νομολογία", "Ανάλυση",
  "Αντεπιχειρήματα", "Κίνδυνοι", "Επόμενα Βήματα", "Πηγές", "Αποποίηση Ευθύνης",
];
const REPORT_SECTIONS_EN = [
  "Executive Summary", "Facts", "Legal Issues",
  "Applicable Cyprus Law", "Case Law", "Analysis",
  "Counterarguments", "Risks", "Next Steps", "Sources", "Disclaimer",
];

// Sample preview content for the "Same rigorous structure" output cards.
// Each card shows the first paragraph of what an actual report section
// looks like so visitors get a tangible feel for the output.
const OUTPUT_SAMPLES_EL = [
  { Icon: ClipboardList, title: "Εκτελεστική Περίληψη", preview: "Συνοπτική αξιολόγηση: η καταγγελία της σύμβασης εμφανίζεται ως άκυρη βάσει του άρθρου 5 του Κεφ. 24. Ο εργαζόμενος διατηρεί δικαίωμα αποζημίωσης.", meta: "≈ 180 λέξεις", pct: 92 },
  { Icon: FileSearch,    title: "Γεγονότα",              preview: "Ο εργαζόμενος προσελήφθη το 2019 με σύμβαση αορίστου χρόνου. Η καταγγελία πραγματοποιήθηκε προφορικά, χωρίς προειδοποίηση και χωρίς αιτιολογία.", meta: "8 γεγονότα", pct: 100 },
  { Icon: Microscope,    title: "Νομικά Ζητήματα",        preview: "Πρώτο: εάν συντρέχει νόμιμος λόγος καταγγελίας. Δεύτερο: εάν τηρήθηκε η περίοδος προειδοποίησης. Τρίτο: εάν οφείλεται αποζημίωση.", meta: "3 ζητήματα", pct: 95 },
  { Icon: Scale,         title: "Εφαρμοστέο Κυπριακό Δίκαιο", preview: "Ο περί Τερματισμού Απασχολήσεως Νόμος, Κεφ. 24, άρθρα 3 και 5. Σύνταγμα, άρθρο 26 (δικαίωμα στην εργασία).", meta: "2 νόμοι", pct: 88 },
  { Icon: BookOpen,      title: "Νομολογία",              preview: "Παπαδόπουλος v. Cyprus Cement Co., Α.Ε. 234/2018: ορισμός νόμιμου λόγου καταγγελίας. Στυλιανού v. Bank of Cyprus, 2020: υπολογισμός αποζημίωσης.", meta: "4 αποφάσεις", pct: 90 },
  { Icon: Quote,         title: "Ανάλυση",                preview: "Η απουσία γραπτής αιτιολόγησης συνιστά παραβίαση του άρθρου 5(1). Ο εργοδότης δεν τήρησε ούτε την ελάχιστη προειδοποίηση των 8 εβδομάδων.", meta: "≈ 420 λέξεις", pct: 94 },
  { Icon: Shield,        title: "Αντεπιχειρήματα",         preview: "Πιθανός ισχυρισμός του εργοδότη για σοβαρό παράπτωμα δεν τεκμηριώνεται έγγραφα. Η αρχή της αναλογικότητας δεν τηρήθηκε.", meta: "2 αντεπιχειρήματα", pct: 80 },
  { Icon: AlertTriangle, title: "Κίνδυνοι",                preview: "Παραγραφή: η αγωγή πρέπει να ασκηθεί εντός 12 μηνών. Πιθανή εκκρεμοδικία στο Δικαστήριο Εργατικών Διαφορών.", meta: "Κρίσιμη προθεσμία", pct: 85 },
  { Icon: ListChecks,    title: "Επόμενα Βήματα",          preview: "1) Συλλογή γραπτής μαρτυρίας. 2) Αίτηση στο Δικαστήριο Εργατικών Διαφορών. 3) Υπολογισμός ακριβούς ποσού αποζημίωσης βάσει μισθού.", meta: "5 βήματα", pct: 96 },
  { Icon: Info,          title: "Πηγές",                  preview: "Νομοθεσία: cylaw.org. Νομολογία: Cylii. Σχετική ΕΣΔΑ απόφαση: HUDOC database. Όλες οι αναφορές με σύνδεσμο.", meta: "11 αναφορές", pct: 100 },
  { Icon: Gavel,         title: "Αποποίηση Ευθύνης",       preview: "Η παρούσα ανάλυση παρέχεται για ενημερωτικούς σκοπούς. Δεν αντικαθιστά τη νομική κρίση δικηγόρου και δεν συνιστά νομική συμβουλή.", meta: "Standard", pct: 100 },
];

const OUTPUT_SAMPLES_EN = [
  { Icon: ClipboardList, title: "Executive Summary",      preview: "At a glance: the termination appears void under section 5 of Cap. 24. The employee retains a claim to statutory compensation.", meta: "~ 180 words", pct: 92 },
  { Icon: FileSearch,    title: "Facts",                  preview: "Hired in 2019 on an indefinite-term contract. Termination was delivered orally, without notice or written justification.", meta: "8 facts", pct: 100 },
  { Icon: Microscope,    title: "Legal Issues",            preview: "First, whether a lawful ground existed. Second, whether the statutory notice period was observed. Third, the quantum of compensation.", meta: "3 issues", pct: 95 },
  { Icon: Scale,         title: "Applicable Cyprus Law",   preview: "Termination of Employment Law, Cap. 24, sections 3 and 5. Constitution, article 26 (right to work).", meta: "2 statutes", pct: 88 },
  { Icon: BookOpen,      title: "Case Law",                preview: "Papadopoulos v. Cyprus Cement Co., A.E. 234/2018: definition of lawful ground. Stylianou v. Bank of Cyprus, 2020: compensation calculation.", meta: "4 decisions", pct: 90 },
  { Icon: Quote,         title: "Analysis",                preview: "The absence of written justification breaches section 5(1). The employer also failed to observe the minimum 8-week notice period.", meta: "~ 420 words", pct: 94 },
  { Icon: Shield,        title: "Counterarguments",        preview: "A possible employer claim of gross misconduct is unsupported by documentary evidence. The proportionality principle was not observed.", meta: "2 counterargs", pct: 80 },
  { Icon: AlertTriangle, title: "Risks",                   preview: "Limitation: the action must be filed within 12 months. Possible parallel proceedings before the Industrial Disputes Court.", meta: "Critical deadline", pct: 85 },
  { Icon: ListChecks,    title: "Next Steps",              preview: "1) Gather written witness statements. 2) File at the Industrial Disputes Court. 3) Calculate exact compensation based on salary.", meta: "5 steps", pct: 96 },
  { Icon: Info,          title: "Sources",                 preview: "Legislation: cylaw.org. Case law: Cylii. Relevant ECHR decision: HUDOC. Every reference is hyperlinked.", meta: "11 references", pct: 100 },
  { Icon: Gavel,         title: "Disclaimer",              preview: "This analysis is provided for informational purposes only. It does not replace a lawyer's judgement and does not constitute legal advice.", meta: "Standard", pct: 100 },
];

const COURTHOUSE_IMG = "https://images.pexels.com/photos/15452110/pexels-photo-15452110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DOC_IMG = "https://images.pexels.com/photos/7567600/pexels-photo-7567600.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OFFICE_IMG = "https://images.pexels.com/photos/7841445/pexels-photo-7841445.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
import PracticeMatrix from "../components/PracticeMatrix";
import CountUp from "../components/CountUp";
import H2Accent from "../components/H2Accent";
import Hero3DPreview from "../components/Hero3DPreview";
import LawAIScene3D from "../components/LawAIScene3D";
import TrustedByStrip from "../components/TrustedByStrip";

function PricingCard({ tier, price, period, features, highlighted, ctaLabel, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-8 border transition ${
        highlighted
          ? "bg-[#0A192F] text-white border-[#0A192F] shadow-[0_28px_60px_rgba(10,25,47,0.28)]"
          : "bg-white text-slate-900 border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1"
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-6 bg-[#8B1E3F] text-white text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1 rounded-full shadow-lg shadow-[#8B1E3F]/30">
          Popular
        </div>
      )}
      <div className={`text-xs font-bold tracking-[0.22em] uppercase ${highlighted ? "text-[#8B1E3F]" : "text-[#8B1E3F]"}`}>{tier}</div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="font-display font-extrabold text-5xl tracking-tighter">€{price}</span>
        <span className={`text-sm ${highlighted ? "text-blue-100" : "text-slate-500"}`}>{period}</span>
      </div>
      <ul className={`mt-6 space-y-2.5 text-sm ${highlighted ? "text-blue-50" : "text-slate-600"}`}>
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check className={`h-4 w-4 mt-0.5 flex-none ${highlighted ? "text-[#8B1E3F]" : "text-[#8B1E3F]"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/signup"
        className={`mt-7 block text-center rounded-full px-5 py-3 font-semibold transition ${
          highlighted ? "bg-[#8B1E3F] hover:bg-[#A22647] text-white shadow-lg shadow-[#8B1E3F]/30" : "btn-primary"
        }`}
        data-testid={`${testid}-cta`}
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

export default function Landing() {
  const { t, lang } = useI18n();
  const [yearly, setYearly] = useState(false);
  const sections = lang === "el" ? REPORT_SECTIONS_EL : REPORT_SECTIONS_EN;

  const pricing = {
    free:        t("landing.planFeaturesFree"),
    starter:     t("landing.planFeaturesStarter"),
    pro:         t("landing.planFeaturesPro"),
    enterprise:  t("landing.planFeaturesEnterprise"),
  };

  return (
    <>
      <Navbar />
      <main className="bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden hero-halos">
          <div className="absolute inset-0 dotted-texture opacity-30 pointer-events-none" />

          <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-24 relative">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              {/* Text + CTAs (left, larger) */}
              <div className="lg:col-span-7 rise-in">
                <div className="kicker">{t("landing.heroKicker")}</div>
                <h1 className="mt-5 font-display font-extrabold text-[44px] sm:text-5xl lg:text-[56px] tracking-tighter text-[#0A192F] leading-[1.05]">
                  {t("landing.heroTitleA")}<span className="text-[#8B1E3F]">{t("landing.heroTitleAccent")}</span>{t("landing.heroTitleB")}<span className="text-[#8B1E3F]">{t("landing.heroTitleAccent2")}</span>{t("landing.heroTitleC")}
                </h1>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                  {t("landing.heroSubtitle")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link to="/signup" className="btn-primary rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2" data-testid="hero-cta-primary">
                    {t("landing.heroCta")} <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a href="#sample" className="btn-ghost rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2" data-testid="hero-cta-secondary">
                    {t("landing.heroSecondary")}
                  </a>
                </div>

                {/* Trust signals row */}
                <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                  <div className="flex flex-col gap-1.5" data-testid="trust-signal-laws">
                    <div className="font-display font-extrabold text-2xl text-[#0A192F] tabular-nums">
                      <CountUp value={60} suffix="+" duration={1400} />
                    </div>
                    <div className="text-[11px] tracking-wide uppercase font-bold text-slate-500 leading-tight">
                      {lang === "el" ? "Κυπριακοί νόμοι" : "Cyprus laws"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5" data-testid="trust-signal-cases">
                    <div className="font-display font-extrabold text-2xl text-[#0A192F] tabular-nums">
                      <CountUp value={2400} suffix="+" duration={1400} />
                    </div>
                    <div className="text-[11px] tracking-wide uppercase font-bold text-slate-500 leading-tight">
                      {lang === "el" ? "Αποφάσεις δικαστηρίων" : "Court decisions"}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5" data-testid="trust-signal-time">
                    <div className="font-display font-extrabold text-2xl text-[#0A192F] tabular-nums">
                      &lt;<CountUp value={90} duration={1400} />s
                    </div>
                    <div className="text-[11px] tracking-wide uppercase font-bold text-slate-500 leading-tight">
                      {lang === "el" ? "Έως πρώτο πρόχειρο" : "To first draft"}
                    </div>
                  </div>
                </div>

                <p className="mt-8 text-xs text-slate-500">{t("landing.heroTrust")}</p>
              </div>

              {/* 3D Law + AI scene (right, smaller) */}
              <div className="lg:col-span-5 rise-in delay-200">
                <LawAIScene3D lang={lang} />
              </div>
            </div>
          </div>

          {/* In-product proof: chat-style preview below the 3D scene */}
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-16 relative">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5">
                <div className="kicker">{lang === "el" ? "Πώς δείχνει στην πράξη" : "What you'll see"}</div>
                <h3 className="mt-3 font-display font-bold text-2xl sm:text-3xl text-[#0A192F] tracking-tight">
                  {lang === "el"
                    ? "Από ερώτημα δικηγόρου σε τεκμηριωμένη απάντηση"
                    : "From a lawyer's question to a referenced answer"}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  {lang === "el"
                    ? "Καμία γενική απάντηση. Κάθε αναφορά είναι αγκυρωμένη σε άρθρα νόμου της Κύπρου, σε νομολογία και σε ρητές προθεσμίες."
                    : "No generic answers. Every reply is anchored to Cyprus statutes, case law and explicit deadlines."}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#FBE9EE] text-[#8B1E3F] font-semibold">{lang === "el" ? "Κεφ. 24" : "Cap. 24"}</span>
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-[#FBE9EE] text-[#8B1E3F] font-semibold">{lang === "el" ? "Παπαδόπουλος ν. CCC, 2018" : "Papadopoulos v. CCC, 2018"}</span>
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200">{lang === "el" ? "Βεβαιότητα 94%" : "Confidence 94%"}</span>
                </div>
              </div>
              <div className="lg:col-span-7">
                <Hero3DPreview lang={lang} />
              </div>
            </div>
          </div>

          {/* STATS ROW under hero */}
          <div className="max-w-7xl mx-auto px-6 md:px-10 pb-20 relative">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { Icon: Layers,    tile: "icon-tile-orange", num: 11, suffix: "",  label: lang === "el" ? "Ενότητες ανά αναφορά" : "Sections per report" },
                { Icon: Briefcase, tile: "icon-tile-blue",   num: 60, suffix: "+", label: lang === "el" ? "Νόμοι & αποφάσεις" : "Laws & cases indexed" },
                { Icon: FileCheck2, tile: "icon-tile-purple",num: 3,  suffix: "",   label: lang === "el" ? "Πηγές δεδομένων" : "Live data sources" },
                { Icon: Sparkles,  tile: "icon-tile-green",  num: 2,  suffix: "m",  prefix: "<", label: lang === "el" ? "Χρόνος δημιουργίας" : "Time to first draft" },
              ].map(({ Icon, tile, num, suffix, prefix, label }, i) => (
                <div key={label} className={`rise-in delay-${(i+2)*100} card-premium p-5 flex items-center gap-4`}>
                  <div className={`icon-tile ${tile}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <CountUp
                      value={num}
                      prefix={prefix || ""}
                      suffix={suffix}
                      className="font-display font-extrabold text-3xl text-[#0A192F] leading-none tabular-nums"
                    />
                    <div className="mt-1 text-xs text-slate-500 leading-tight">{label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <TrustedByStrip lang={lang} />

        <div className="section-divider max-w-7xl mx-auto" />

        {/* VALUE PROPS */}
        <section id="features" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-3xl">
            <div className="kicker">{t("landing.valueKicker")}</div>
            <H2Accent width={84}>
              <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
                {t("landing.valueTitle")}
              </h2>
            </H2Accent>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {[
              { Icon: Layers, tile: "icon-tile-orange", title: t("landing.value1Title"), desc: t("landing.value1Desc") },
              { Icon: Scale,  tile: "icon-tile-blue",   title: t("landing.value2Title"), desc: t("landing.value2Desc") },
              { Icon: Shield, tile: "icon-tile-purple", title: t("landing.value3Title"), desc: t("landing.value3Desc") },
              { Icon: Users,  tile: "icon-tile-green",  title: t("landing.value4Title"), desc: t("landing.value4Desc") },
            ].map(({ Icon, tile, title, desc }, i) => (
              <div key={i} className="card-premium p-8" data-testid={`value-card-${i}`}>
                <div className={`icon-tile ${tile}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mt-5 font-display font-bold text-2xl text-[#0A192F]">{title}</div>
                <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how" className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <div className="kicker">{t("landing.howKicker")}</div>
                <H2Accent width={72}>
                  <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
                    {t("landing.howTitle")}
                  </h2>
                </H2Accent>
                <div className="mt-10 space-y-8">
                  {[
                    { n: "01", t: t("landing.how1"), d: t("landing.how1d") },
                    { n: "02", t: t("landing.how2"), d: t("landing.how2d") },
                    { n: "03", t: t("landing.how3"), d: t("landing.how3d") },
                  ].map((s, i) => (
                    <div key={s.n} className="flex gap-5" data-testid={`how-step-${i}`}>
                      <div className="font-display font-bold text-4xl text-[#0A192F] leading-none">{s.n}</div>
                      <div className="flex-1">
                        <div className="font-display font-bold text-xl text-slate-900">{s.t}</div>
                        <div className="mt-2 text-slate-600 leading-relaxed">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
                  <img src={DOC_IMG} alt="Legal document" className="w-full h-[520px] object-cover" />
                  <div className="absolute left-6 bottom-6 bg-white/95 backdrop-blur rounded-xl border border-slate-200 p-4 max-w-xs">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">Input → Output</div>
                    <div className="mt-1 font-display font-bold text-lg text-slate-900">
                      {lang === "el" ? "Δομημένη αναφορά, έτοιμη για το γραφείο." : "Structured report, ready for your firm."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* OUTPUT PREVIEW */}
        <section id="sample" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-3xl">
            <div className="kicker">{t("landing.outputKicker")}</div>
            <H2Accent width={84}>
              <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
                {t("landing.outputTitle")}
              </h2>
            </H2Accent>
            <p className="mt-4 text-slate-600">{t("landing.outputSub")}</p>
          </div>

          {/* Sample matter header */}
          <div className="mt-12 mb-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-[#0A192F] to-[#0F2545] text-white px-6 py-5 shadow-[0_20px_50px_rgba(10,25,47,0.18)]">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Scale className="h-5 w-5 text-[#F8DBE4]" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#F8DBE4]">{lang === "el" ? "Δείγμα υπόθεσης" : "Sample matter"}</div>
                <div className="font-display font-bold text-lg">{lang === "el" ? "Καταγγελία σύμβασης εργασίας · Κεφ. 24" : "Employment termination · Cap. 24"}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                <span className="font-mono tracking-wide">{lang === "el" ? "Υψηλή βεβαιότητα" : "High confidence"}</span>
              </div>
              <div className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5">
                <span className="font-mono tracking-wide">11 / 11</span>
                <span className="text-blue-200">{lang === "el" ? "ενότητες" : "sections"}</span>
              </div>
            </div>
          </div>

          {/* 11 section cards with real preview content */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(lang === "el" ? OUTPUT_SAMPLES_EL : OUTPUT_SAMPLES_EN).map((sec, i) => (
              <div
                key={sec.title}
                className="card-premium p-5 group hover:-translate-y-1 transition flex flex-col"
                data-testid={`output-sec-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[11px] tracking-[0.18em] font-bold text-[#8B1E3F]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <sec.Icon className="h-4 w-4 text-slate-300 group-hover:text-[#8B1E3F] transition" />
                </div>
                <div className="mt-3 font-display font-bold text-[17px] leading-snug text-slate-900">
                  {sec.title}
                </div>
                <p className="mt-2.5 text-[13px] text-slate-600 leading-relaxed flex-1">
                  {sec.preview}
                </p>
                {sec.meta && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                    <span className="font-mono text-slate-500">{sec.meta}</span>
                    <span className="font-bold text-[#8B1E3F]">{sec.pct}%</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA below cards */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="btn-primary rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2"
              data-testid="output-cta-signup"
            >
              <Sparkles className="h-4 w-4" />
              {lang === "el" ? "Δοκιμάστε με τη δική σας υπόθεση" : "Try it on your own matter"}
            </Link>
            <div className="text-xs text-slate-500">
              {lang === "el" ? "Δωρεάν δοκιμή. Χωρίς πιστωτική κάρτα." : "Free trial. No credit card."}
            </div>
          </div>
        </section>

        {/* USE CASES */}
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-4 lg:sticky lg:top-24">
                <div className="kicker">{t("landing.useCasesKicker")}</div>
                <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
                  {t("landing.useCasesTitle")}
                </h2>
                <div className="mt-8">
                  <PracticeMatrix />
                </div>
              </div>
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                {[
                  { Icon: Building2, tile: "icon-tile-orange", t: t("landing.uc1"), d: t("landing.uc1d") },
                  { Icon: Briefcase, tile: "icon-tile-blue",   t: t("landing.uc2"), d: t("landing.uc2d") },
                  { Icon: Home,      tile: "icon-tile-purple", t: t("landing.uc3"), d: t("landing.uc3d") },
                  { Icon: Heart,     tile: "icon-tile-pink",   t: t("landing.uc4"), d: t("landing.uc4d") },
                  { Icon: Gavel,     tile: "icon-tile-amber",  t: t("landing.uc5"), d: t("landing.uc5d") },
                  { Icon: FileCheck2,tile: "icon-tile-teal",   t: t("landing.uc6"), d: t("landing.uc6d") },
                ].map(({ Icon, tile, t: title, d }, i) => (
                  <div key={i} className="card-premium p-6" data-testid={`uc-card-${i}`}>
                    <div className={`icon-tile ${tile}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="mt-4 font-display font-bold text-lg text-[#0A192F]">{title}</div>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <div className="kicker">{t("landing.pricingKicker")}</div>
              <H2Accent width={84}>
                <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
                  {t("landing.pricingTitle")}
                </h2>
              </H2Accent>
            </div>
            <div className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1" data-testid="pricing-toggle">
              <button
                onClick={() => setYearly(false)}
                data-testid="pricing-monthly"
                className={`px-4 py-2 text-sm rounded-full transition ${!yearly ? "bg-[#0A192F] text-white" : "text-slate-600"}`}
              >
                {t("landing.pricingMonthly")}
              </button>
              <button
                onClick={() => setYearly(true)}
                data-testid="pricing-yearly"
                className={`px-4 py-2 text-sm rounded-full transition ${yearly ? "bg-[#0A192F] text-white" : "text-slate-600"}`}
              >
                {t("landing.pricingYearly")}
              </button>
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PricingCard
              tier={t("landing.pricingFreeName")}
              price={0}
              period={yearly ? t("landing.pricingPerYear") : t("landing.pricingPerMonth")}
              features={pricing.free}
              ctaLabel={t("landing.pricingFreeCta")}
              testid="price-free"
            />
            <PricingCard
              tier="Starter"
              price={yearly ? 70 : 7}
              period={yearly ? t("landing.pricingPerYear") : t("landing.pricingPerMonth")}
              features={pricing.starter}
              ctaLabel={t("landing.pricingStarterCta")}
              testid="price-starter"
            />
            <PricingCard
              tier="Professional"
              price={yearly ? 190 : 19}
              period={yearly ? t("landing.pricingPerYear") : t("landing.pricingPerMonth")}
              features={pricing.pro}
              highlighted
              ctaLabel={t("landing.pricingProCta")}
              testid="price-pro"
            />
            <PricingCard
              tier="Enterprise"
              price={yearly ? 490 : 49}
              period={yearly ? t("landing.pricingPerYear") : t("landing.pricingPerMonth")}
              features={pricing.enterprise}
              ctaLabel={t("landing.pricingEnterpriseCta")}
              testid="price-enterprise"
            />
          </div>
        </section>

        {/* CTA BANNER */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
          <div className="rounded-3xl bg-[#0A192F] overflow-hidden relative">
            <div className="absolute inset-0 opacity-20"
                 style={{ backgroundImage: `url(${COURTHOUSE_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-transparent" />
            <div className="relative p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7DD3DC]">Dikigoros.io</div>
                <h3 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">
                  {t("landing.ctaTitle")}
                </h3>
                <p className="mt-3 text-blue-100 max-w-lg">{t("landing.ctaSubtitle")}</p>
              </div>
              <div className="md:justify-self-end">
                <Link to="/signup" className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm"
                      data-testid="footer-cta">
                  <Sparkles className="h-4 w-4" /> {t("landing.ctaButton")}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
